import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Header } from "@/pages/Vitals";
import {
  Activity, Droplet, Scale, HeartPulse, Wind, Thermometer, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend,
} from "recharts";

const RANGES = [
  { key: "7", label: "7d", days: 7 },
  { key: "30", label: "30d", days: 30 },
  { key: "90", label: "90d", days: 90 },
  { key: "365", label: "1y", days: 365 },
  { key: "all", label: "All", days: null },
];

const METRICS = [
  { key: "bp",          label: "Blood pressure", unit: "mmHg",  icon: Activity,    color: "#4A6750", normal: { min: 90, max: 120 } },
  { key: "sugar",       label: "Blood sugar",    unit: "mg/dL", icon: Droplet,     color: "#C76B58", normal: { min: 70, max: 140 } },
  { key: "weight",      label: "Weight",         unit: "kg",    icon: Scale,       color: "#5B7582", normal: null },
  { key: "heart_rate",  label: "Heart rate",     unit: "bpm",   icon: HeartPulse,  color: "#A85546", normal: { min: 60, max: 100 } },
  { key: "oxygen",      label: "SpO₂",           unit: "%",     icon: Wind,        color: "#5B8266", normal: { min: 95, max: 100 } },
  { key: "temperature", label: "Temperature",    unit: "°F",    icon: Thermometer, color: "#D8A24A", normal: { min: 97, max: 99 } },
];

function fmtDay(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`; }
  catch { return iso; }
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Trends() {
  const { active: activeMember } = useMembers();
  const [range, setRange] = useState("30");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vitals", {
        params: {
          member_id: activeMember?.id,
          start: range === "all" ? undefined : isoDaysAgo(parseInt(range, 10)),
        },
      });
      const sorted = [...r.data].sort((a, b) => a.date.localeCompare(b.date));
      setRows(sorted);
    } finally { setLoading(false); }
  }, [activeMember?.id, range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="trends-page">
      <Header
        title="Trends"
        subtitle={`How ${activeMember?.is_self ? "your" : activeMember?.name + "'s"} numbers have moved over time.`}
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="range-bar">
        {RANGES.map(r => (
          <Button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
            variant={range === r.key ? "default" : "outline"}
            className={`rounded-full h-8 px-3 text-xs ${range === r.key ? "bg-[#4A6750] hover:bg-[#3B5340]" : "border-[#E5E7E4]"}`}>
            {r.label}
          </Button>
        ))}
        <span className="text-xs text-[#7D8F84] ml-auto">{rows.length} reading{rows.length === 1 ? "" : "s"}</span>
      </div>

      {loading && <p className="text-[#7D8F84] text-sm">Loading…</p>}
      {!loading && rows.length === 0 && (
        <Card className="p-10 rounded-2xl border-[#E5E7E4] text-center" data-testid="empty-state">
          <p className="font-heading text-lg font-medium">No readings in this period.</p>
          <p className="text-sm text-[#7D8F84] mt-1">Log vitals from the Vitals page to see trends here.</p>
        </Card>
      )}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {METRICS.map(m => <MetricCard key={m.key} metric={m} rows={rows} />)}
        </div>
      )}
    </div>
  );
}

function MetricCard({ metric, rows }) {
  const series = useMemo(() => {
    if (metric.key === "bp") {
      return rows
        .filter(r => r.bp_systolic != null && r.bp_diastolic != null)
        .map(r => ({ date: r.date, label: fmtDay(r.date), sys: r.bp_systolic, dia: r.bp_diastolic }));
    }
    return rows
      .filter(r => r[metric.key] != null)
      .map(r => ({ date: r.date, label: fmtDay(r.date), v: Number(r[metric.key]) }));
  }, [rows, metric.key]);

  const stats = useMemo(() => calcStats(series, metric.key), [series, metric.key]);

  if (series.length === 0) {
    return (
      <Card className="p-5 rounded-2xl border-[#E5E7E4]" data-testid={`metric-${metric.key}`}>
        <MetricHeader metric={metric} stats={null} />
        <div className="h-40 flex items-center justify-center text-sm text-[#7D8F84] italic">No data for this metric.</div>
      </Card>
    );
  }

  return (
    <Card className="p-5 rounded-2xl border-[#E5E7E4]" data-testid={`metric-${metric.key}`}>
      <MetricHeader metric={metric} stats={stats} />
      <div className="h-56 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          {metric.key === "bp"
            ? <BPChart series={series} color={metric.color} normal={metric.normal} />
            : <SimpleChart series={series} color={metric.color} normal={metric.normal} />}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function MetricHeader({ metric, stats }) {
  const Icon = metric.icon;
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
           style={{ background: `${metric.color}18` }}>
        <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: metric.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading text-base font-semibold leading-tight">{metric.label}</p>
        <p className="text-xs text-[#7D8F84]">{metric.unit}</p>
      </div>
      {stats && (
        <div className="text-right flex-shrink-0">
          <div className="flex items-center gap-1 justify-end">
            <TrendIcon dir={stats.trend} />
            <span className="font-heading text-xl font-semibold leading-tight">{stats.last}</span>
          </div>
          <p className="text-[10px] text-[#7D8F84] mt-0.5">
            min {stats.min} · avg {stats.avg} · max {stats.max}
          </p>
        </div>
      )}
    </div>
  );
}

function TrendIcon({ dir }) {
  if (dir === "up")   return <TrendingUp className="w-3.5 h-3.5 text-[#C76B58]" />;
  if (dir === "down") return <TrendingDown className="w-3.5 h-3.5 text-[#5B8266]" />;
  return <Minus className="w-3.5 h-3.5 text-[#7D8F84]" />;
}

const CHART_MARGIN = { top: 6, right: 10, left: -22, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#7D8F84" };
const Y_DOMAIN_LARGE = ["dataMin - 10", "dataMax + 10"];
const Y_DOMAIN_SMALL = ["dataMin - 2", "dataMax + 2"];
const LEGEND_WRAPPER = { fontSize: 11 };
const REF_LINE_PROPS = { stroke: "#C76B58", strokeDasharray: "4 4", strokeOpacity: 0.5 };
const ACTIVE_DOT_LG = { r: 5 };

function BPChart({ series, color, normal }) {
  const sysDot = { r: 3, fill: color };
  const diaDot = { r: 3, fill: "#5B7582" };
  return (
    <LineChart data={series} margin={CHART_MARGIN}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E4" vertical={false} />
      <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" />
      <YAxis tick={AXIS_TICK} domain={Y_DOMAIN_LARGE} />
      <Tooltip content={<TipCard suffix="mmHg" />} />
      <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
      {normal && <ReferenceLine y={normal.max} {...REF_LINE_PROPS} />}
      <Line dataKey="sys" name="Systolic"  stroke={color} strokeWidth={2.5} dot={sysDot} activeDot={ACTIVE_DOT_LG} />
      <Line dataKey="dia" name="Diastolic" stroke="#5B7582" strokeWidth={2} dot={diaDot} activeDot={ACTIVE_DOT_LG} strokeDasharray="6 3" />
    </LineChart>
  );
}

function SimpleChart({ series, color, normal }) {
  const gid = `g-${color.replace("#", "")}`;
  const dot = { r: 3, fill: color, strokeWidth: 0 };
  return (
    <AreaChart data={series} margin={CHART_MARGIN}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E4" vertical={false} />
      <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" />
      <YAxis tick={AXIS_TICK} domain={Y_DOMAIN_SMALL} />
      <Tooltip content={<TipCard />} />
      {normal && <ReferenceLine y={normal.max} {...REF_LINE_PROPS} />}
      {normal && <ReferenceLine y={normal.min} {...REF_LINE_PROPS} />}
      <Area dataKey="v" stroke={color} strokeWidth={2.5} fill={`url(#${gid})`}
            dot={dot} activeDot={ACTIVE_DOT_LG} />
    </AreaChart>
  );
}

function TipCard({ active, payload, label, suffix }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl bg-white border border-[#E5E7E4] shadow-sm px-3 py-2 text-xs" data-testid="chart-tip">
      <p className="font-medium text-[#1C2E24]">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <b>{p.value}</b>{suffix ? ` ${suffix}` : ""}
        </p>
      ))}
    </div>
  );
}

function calcStats(series, key) {
  const vals = key === "bp"
    ? series.map(s => s.sys)
    : series.map(s => s.v);
  if (!vals.length) return null;
  const last = vals[vals.length - 1];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const avg = +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  let trend = "flat";
  if (vals.length >= 2) {
    const prev = vals[vals.length - 2];
    if (last - prev > 0.05 * prev) trend = "up";
    else if (prev - last > 0.05 * prev) trend = "down";
  }
  return { last, min: +min.toFixed(1), max: +max.toFixed(1), avg, trend };
}
